import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { customersAPI, adminAPI, usersAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CountrySelect } from '../components/CountrySelect';
import { AddressFields } from '../components/AddressFields';
import { ArrowLeft, Save, Loader2, AlertCircle, Building2, MapPin, User, Settings, Info } from 'lucide-react';
import { toast } from 'sonner';

const NAVY = 'hsl(var(--c4p-navy-900))';
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const PARTNER_TYPES = ['SAP Partner', 'VAR', 'ISV', 'MSP', 'System Integrator', 'Reseller', 'Distributor', 'Other'];

function SectionCard({ icon: Icon, iconBg, iconColor, title, subtitle, children }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div className="px-5 py-3.5 flex items-center gap-3"
        style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.4)' }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
          <Icon size={14} style={{ color: iconColor }} />
        </div>
        <div>
          <p className="text-sm font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{title}</p>
          {subtitle && <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{subtitle}</p>}
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

export default function PartnerCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') || null;

  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);
  const [psmUsers, setPsmUsers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(['Direct Debit', 'Wise', 'Bank Transfer', 'Credit Card', 'BACS']);

  const [form, setForm] = useState({
    company_name: '',
    partner_type: 'SAP Partner',
    website: '',
    country: '',
    default_currency: 'GBP',
    default_region_id: '',
    default_region_name: '',
    default_region_aws_code: '',
    default_psm_id: '',
    default_psm_name: '',
    default_payment_method: '',
    // Structured address (consumed directly by AddressFields via form.addr_*)
    addr_line1: '', addr_line2: '', addr_city: '', addr_region: '', addr_postcode: '',
    // Primary contact
    contact_name: '', contact_title: '', contact_email: '', contact_phone: '',
    notes: '',
  });

  const set = (updates) => setForm(prev => ({ ...prev, ...updates }));

  // AddressFields reads form.addr_* and form.country directly
  const hasAddress = [form.addr_line1, form.addr_city, form.addr_postcode].some(v => v?.trim());
  const hasContact = form.contact_name?.trim() || form.contact_email?.trim();

  useEffect(() => {
    adminAPI.getRegions().then(r => setRegions(r.data || [])).catch(() => {});
    usersAPI.directory('SALES').then(r => setPsmUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminAPI.getDropdownOptions('payment_method')
      .then(r => { const opts = (r.data || []).map(o => o.label); if (opts.length) setPaymentMethods(opts); })
      .catch(() => {});
  }, []);

  const handleRegionChange = (id) => {
    const r = regions.find(r => r.id === id);
    setForm(f => ({
      ...f,
      default_region_id: id,
      default_region_name: r?.name || '',
      default_region_aws_code: r?.aws_region_code || '',
      default_currency: r?.default_currency || f.default_currency,
    }));
  };

  const handleSave = async () => {
    if (!form.company_name?.trim()) { toast.error('Company name is required'); return; }
    if (!form.country) { toast.error('Country is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Build contacts array if a primary contact was provided
        contacts: hasContact ? [{
          name: form.contact_name || '',
          title: form.contact_title || '',
          email: form.contact_email || '',
          phone: form.contact_phone || '',
          is_primary: true,
          is_active: true,
          contact_type: 'general',
          notes: '',
        }] : [],
      };
      const { data: newPartner } = await customersAPI.create(payload);
      toast.success(`Partner "${newPartner.company_name}" created`);

      if (returnTo) {
        // Return to wizard (or wherever) with the new partner auto-selected
        navigate(`${returnTo}?preset_partner=${newPartner.id}`);
      } else {
        navigate(`/customers/${newPartner.id}`);
      }
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Failed to create partner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }}>
      {/* Header */}
      <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <div className="px-8 pt-6 pb-4 flex items-start justify-between gap-4">
          <div>
            <button className="flex items-center gap-1.5 text-xs font-medium mb-3 hover:opacity-70 transition-opacity"
              style={{ color: 'hsl(var(--muted-fg))' }}
              onClick={() => navigate(returnTo || '/customers')}>
              <ArrowLeft size={13} /> {returnTo ? 'Back to Quote' : 'Partners'}
            </button>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
              New Partner
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
              {returnTo
                ? 'Complete the partner profile below. You\'ll be returned to the quote wizard after saving.'
                : 'Create a new partner record. You can add additional contacts and upload the MSA after saving.'}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2 flex-shrink-0"
            style={{ background: NAVY, color: 'white' }} data-testid="partner-create-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
            Create Partner
          </Button>
        </div>
        {returnTo && (
          <div className="px-8 pb-3">
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' }}>
              <Info size={12} />
              After creating the partner, you'll be taken back to the quote wizard with this partner pre-selected.
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 px-8 py-6">
        <div className="max-w-3xl mx-auto space-y-5">

          {/* Company Details */}
          <SectionCard icon={Building2} iconBg="#eff6ff" iconColor="#1e40af"
            title="Company Details" subtitle="Core information about this partner">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-medium">
                  Company Name <span style={{ color: 'hsl(var(--danger))' }}>*</span>
                </Label>
                <Input value={form.company_name}
                  onChange={e => set({ company_name: e.target.value })}
                  placeholder="e.g. Acme Solutions Ltd"
                  data-testid="partner-company-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Partner Type</Label>
                <Select value={form.partner_type} onValueChange={v => set({ partner_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Website</Label>
                <Input value={form.website} onChange={e => set({ website: e.target.value })} placeholder="https://" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">
                  Country <span style={{ color: 'hsl(var(--danger))' }}>*</span>
                </Label>
                <CountrySelect value={form.country} onChange={v => set({ country: v })} testId="partner-create-country" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Default Currency</Label>
                <Select value={form.default_currency} onValueChange={v => set({ default_currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Notes</Label>
              <Textarea value={form.notes} onChange={e => set({ notes: e.target.value })}
                className="h-16 resize-none" placeholder="Internal notes about this partner..." />
            </div>
          </SectionCard>

          {/* Quote Defaults */}
          <SectionCard icon={Settings} iconBg="#f0fdf4" iconColor="#16a34a"
            title="Quote Defaults" subtitle="Auto-populated when this partner is selected in a new quote">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-medium">Default AWS Region</Label>
                <Select value={form.default_region_id || 'none'} onValueChange={v => handleRegionChange(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="partner-create-region"><SelectValue placeholder="No default region" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default region</SelectItem>
                    {regions.map(r => <SelectItem key={r.id} value={r.id}>{r.name} · {r.aws_region_code} · {r.default_currency}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Sets the hosting region and currency when this partner is selected.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Default PSM</Label>
                <Select value={form.default_psm_id || 'none'} onValueChange={v => {
                  const u = psmUsers.find(u => u.id === v);
                  set({ default_psm_id: v === 'none' ? '' : v, default_psm_name: u?.name || '' });
                }}>
                  <SelectTrigger><SelectValue placeholder="No default PSM" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default PSM</SelectItem>
                    {psmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Default Payment Method</Label>
                <Select value={form.default_payment_method || 'none'} onValueChange={v => set({ default_payment_method: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="No default" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default</SelectItem>
                    {paymentMethods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </SectionCard>

          {/* Address */}
          <SectionCard icon={MapPin} iconBg="#fef9c3" iconColor="#854d0e"
            title="Registered Address"
            subtitle="Used on quote PDFs and partner summary documents — optional but recommended">
            {!hasAddress && (
              <div className="flex items-start gap-2.5 rounded-lg px-3 py-2.5"
                style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                <AlertCircle size={13} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: '#92400e' }}>
                  Address is optional but <strong>strongly recommended</strong> — it appears on all quote PDFs. You can add it now or edit the partner profile later.
                </p>
              </div>
            )}
            <AddressFields
              form={form}
              setForm={setForm}
            />
          </SectionCard>

          {/* Primary Contact */}
          <SectionCard icon={User} iconBg="#f5f3ff" iconColor="#7c3aed"
            title="Primary Contact"
            subtitle="The main point of contact at this partner — used for signing link emails">
            {!hasContact && (
              <div className="flex items-start gap-2.5 rounded-lg px-3 py-2.5"
                style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                <AlertCircle size={13} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: '#92400e' }}>
                  A contact with an email address is <strong>required</strong> to send signing links. Add one now or add contacts on the partner profile after saving.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Contact Name</Label>
                <Input value={form.contact_name} onChange={e => set({ contact_name: e.target.value })}
                  placeholder="e.g. Jane Smith" data-testid="partner-create-contact-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Job Title</Label>
                <Input value={form.contact_title} onChange={e => set({ contact_title: e.target.value })}
                  placeholder="e.g. Partner Success Director" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Email Address</Label>
                <Input type="email" value={form.contact_email} onChange={e => set({ contact_email: e.target.value })}
                  placeholder="jane@acmesolutions.com" data-testid="partner-create-contact-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Phone</Label>
                <Input value={form.contact_phone} onChange={e => set({ contact_phone: e.target.value })}
                  placeholder="+44 7700 000000" />
              </div>
            </div>
          </SectionCard>

          {/* Save footer */}
          <div className="flex items-center justify-between pt-2 pb-8">
            <Button variant="outline" onClick={() => navigate(returnTo || '/customers')}>
              <ArrowLeft size={13} className="mr-1.5" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2"
              style={{ background: NAVY, color: 'white' }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
              Create Partner
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
